
import { collection, getDocs, Firestore, query, where, orderBy } from "firebase/firestore";
import { SortableOption } from "@/types/fieldsOptions";
import cacheService from "./cacheService";

// Helper function to get options by type with caching
export const getSortableOptions = async (
  db: Firestore,
  type: string
): Promise<SortableOption[]> => {
  try {
    // Check if we have cached options for this type
    const cacheKey = `sortableOptions_${type}`;
    const cachedOptions = cacheService.get<SortableOption[]>(cacheKey);
    
    if (cachedOptions) {
      console.log(`Using cached sortable options for type: ${type}`);
      return cachedOptions;
    }
    
    console.log(`Getting sortable options for type: ${type}`);
    const sortableOptionsRef = collection(db, "sortableOptions");
    const q = query(
      sortableOptionsRef,
      where("type", "==", type),
      orderBy("sortOrder")
    );

    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log(`No sortable options found for type: ${type}`);
      return [];
    }

    const options = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as SortableOption[];

    console.log(`Found ${options.length} sortable options for type: ${type}`);
    
    // Cache the options for 30 minutes
    cacheService.set(cacheKey, options);
    
    return options;
  } catch (error) {
    console.error(`Error getting sortable options for type: ${type}:`, error);
    return [];
  }
};

export const getAvailabilityStatusOptions = async (
  db: Firestore
): Promise<SortableOption[]> => {
  return getSortableOptions(db, "availStatus");
};

export const getSignupStatusOptions = async (
  db: Firestore
): Promise<SortableOption[]> => {
  return getSortableOptions(db, "signUpStatus");
};

// Get all sortable options with caching
export const getAllSortableOptions = async (
  db: Firestore
): Promise<Record<string, SortableOption>> => {
  try {
    const cacheKey = "sortableOptions_all_map";
    const cachedOptions = cacheService.get<Record<string, SortableOption>>(cacheKey);
    
    if (cachedOptions) {
      console.log("Using cached all sortable options map");
      return cachedOptions;
    }
    
    console.log("Getting all sortable options for mapping");
    const sortableOptionsRef = collection(db, "sortableOptions");
    const querySnapshot = await getDocs(sortableOptionsRef);
    
    if (querySnapshot.empty) {
      console.log("No sortable options found");
      return {};
    }
    
    // Create a map with the ID as the key for easy lookup
    const optionsMap: Record<string, SortableOption> = {};
    querySnapshot.docs.forEach((doc) => {
      optionsMap[doc.id] = {
        id: doc.id,
        ...doc.data()
      } as SortableOption;
    });
    
    console.log(`Found ${Object.keys(optionsMap).length} total sortable options`);
    
    // Cache the options map for 30 minutes
    cacheService.set(cacheKey, optionsMap);
    
    return optionsMap;
  } catch (error) {
    console.error("Error getting all sortable options map:", error);
    return {};
  }
};
